/**
 * The three links under the sign card — `docs/proposal.md` §9.4, board 7.
 *
 * A proposal is not a form with one button on it. Ask a question, ask for
 * changes, say no: these are the other three things somebody actually does
 * with a quote, and a page that offers only Accept is one people answer by
 * not answering at all.
 *
 * All three are **one tap from the sign card**, all three send the estimator
 * a message, and only the decline moves the quote. `SheetOrDialog` is the
 * switch — a dialog on a desktop, a bottom sheet on a phone — and the body is
 * the same component either way, which is the same bargain the sign card
 * itself makes.
 *
 * **Nothing here argues.** The decline asks for a reason because a list gets
 * something the company can count where a free box gets "no thanks", and then
 * it accepts the answer: no "are you sure", no last offer, no second screen.
 */
import { useState } from 'react'
import { DECLINE_REASONS, type DeclineReason, type ProposalView } from '@rowted/shared'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { cn } from '../../lib/cn.js'
import { IconCheck } from '../../frame/icons.js'
import { GlyphChat, GlyphCheckBold, GlyphClock, GlyphPhone } from './glyphs.js'

export type ResponseKind = 'question' | 'changes' | 'decline'

export interface ResponseApi {
  question: (body: string) => Promise<void>
  changes: (refs: string[], body: string) => Promise<void>
  decline: (reason: DeclineReason, note: string) => Promise<void>
  /** The closed states' one action; absent where there is nothing to ask. */
  reopen?: (note: string) => Promise<void>
}

const TITLES: Record<ResponseKind, (who: string) => string> = {
  question: (who) => `Ask ${who} a question`,
  changes: () => 'Ask for changes',
  decline: () => 'Decline this proposal?',
}

/** Board 7's line under each title: what happens, and that nothing else does. */
const SUBS: Record<ResponseKind, (who: string) => string> = {
  question: (who) => `${who === 'us' ? 'We get' : `${who} gets`} your question. Your proposal stays open while you wait.`,
  changes: () => 'Tell us what to change and we will send a new version. This one stays valid until then.',
  decline: () => 'No hard feelings. It helps us to know why, and you can change your mind until it expires.',
}

/**
 * The row of three under the sign card.
 *
 * Plain text links rather than buttons, and deliberately: three buttons
 * beside Accept would read as four equal choices, and the page has one
 * recommendation. They are 44px tall all the same, because a target nobody
 * can hit is not a choice either (§13).
 */
export function ResponseLinks({ onOpen }: {
  onOpen: (kind: ResponseKind) => void
}) {
  return (
    <div className="-my-3 flex flex-wrap items-center justify-center gap-x-[22px]">
      {(['question', 'changes', 'decline'] as const).map((kind) => (
        <button
          key={kind}
          type="button"
          onClick={() => onOpen(kind)}
          className={cn('flex h-11 items-center text-[14px] font-semibold',
            kind === 'decline' ? 'text-text-muted' : 'text-brand-tx')}
        >
          {kind === 'question' ? 'I have a question'
            : kind === 'changes' ? 'Ask for changes' : 'Decline'}
        </button>
      ))}
    </div>
  )
}

export function ResponseDialog({ kind, view, api, phone, onClose }: {
  kind: ResponseKind | null
  view: ProposalView
  api: ResponseApi
  phone: boolean
  onClose: () => void
}) {
  if (!kind) return null
  const who = view.estimator?.name.split(/\s+/)[0] ?? 'us'
  return (
    <SheetOrDialog
      phone={phone}
      onClose={onClose}
      title={TITLES[kind](who)}
      sub={SUBS[kind](who)}
      width={600}
    >
      <ResponseBody kind={kind} view={view} api={api} onClose={onClose} />
    </SheetOrDialog>
  )
}

function ResponseBody({ kind, view, api, onClose }: {
  kind: ResponseKind
  view: ProposalView
  api: ResponseApi
  onClose: () => void
}) {
  const [body, setBody] = useState('')
  const [refs, setRefs] = useState<string[]>([])
  const [reason, setReason] = useState<DeclineReason | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  /*
   * **Sent, and the proposal stays open** (§9.2's "question sent" row). The
   * panel says what was asked and when, because the one thing somebody wants
   * after sending a message into a web page is proof that it went.
   */
  if (sent) {
    /*
     * Board 11 cell 3: what happened, what did not (nothing is signed), and
     * the words they sent with when, because the one thing somebody wants
     * after sending a message into a web page is proof that it went.
     */
    const name = view.estimator?.name ?? null
    const first = name?.split(/\s+/)[0] ?? null
    const phone = view.estimator?.phone ?? view.identity.phone
    const now = new Date()
    const when = `${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${
      now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    return (
      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-t2 text-brand-tx">
            {kind === 'decline' ? <IconCheck size={28} /> : <GlyphChat size={28} />}
          </span>
          <h2 className="m-0 text-[26px] font-bold leading-[1.1] tracking-[-0.025em] text-text">
            {kind === 'decline' ? 'Thank you for letting us know' : `Sent to ${first ?? 'the office'}`}
          </h2>
          {kind !== 'decline' && (
            <span className="text-[15px] leading-[1.5] text-text-body">
              {`${first ?? 'We'} will answer${phone ? ' by text or call' : ''}. Your proposal stays open`}
              {view.expiresOn ? ` until ${new Date(`${view.expiresOn.slice(0, 10)}T12:00:00`)
                .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
              {'. Nothing has been signed.'}
            </span>
          )}
        </div>
        {kind !== 'decline' && body.trim() && (
          <div className="flex flex-col gap-2 rounded-[18px] border border-line bg-surface px-[18px] py-4
                          shadow-proposal">
            <span className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-text-faint">
              {kind === 'question' ? 'Your question' : 'What you asked for'}
            </span>
            <p className="m-0 text-[15px] leading-[1.5] text-text">{body.trim()}</p>
            <span className="mt-1 flex items-center gap-1.5 border-t border-line-soft pt-2.5 text-[12.5px]
                             text-text-faint">
              <GlyphClock size={13} />
              {`Sent ${when}${name ? ` to ${name}` : ''}`}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          className="flex h-[54px] w-full items-center justify-center rounded-[14px] bg-brand text-[16px]
                     font-semibold text-brand-on"
        >
          {kind === 'decline' ? 'Close' : 'Back to the proposal'}
        </button>
        {kind !== 'decline' && phone && (
          <a href={`tel:${phone}`}
            className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[14px] border border-line
                       bg-surface text-[14.5px] font-semibold text-text">
            <GlyphPhone size={18} />
            {`Call ${phone}`}
          </a>
        )}
      </div>
    )
  }

  const ready = kind === 'decline' ? reason !== null : body.trim().length > 0
  const who = view.estimator?.name.split(/\s+/)[0] ?? 'us'

  async function send() {
    setBusy(true)
    setError(null)
    try {
      if (kind === 'question') await api.question(body.trim())
      else if (kind === 'changes') await api.changes(refs, body.trim())
      else if (reason) await api.decline(reason, body.trim())
      setSent(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3.5 px-1 py-2">
      {kind === 'changes' && view.visits.length > 0 && (
        <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
          <legend className="sr-only">Which part? Tick any that apply.</legend>
          {view.visits.map((visit) => {
            const on = refs.includes(visit.id)
            return (
              <label key={visit.id} className={choiceRow(on)}>
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={on}
                  onChange={() => setRefs(on
                    ? refs.filter((one) => one !== visit.id)
                    : [...refs, visit.id])}
                />
                <span aria-hidden="true"
                  className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px]',
                    'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-ring',
                    on ? 'bg-brand-ui text-brand-on' : 'border-[1.6px] border-text-faint bg-surface')}>
                  {on && <GlyphCheckBold size={16} />}
                </span>
                {visit.name}
              </label>
            )
          })}
        </fieldset>
      )}

      {kind === 'decline' && (
        <div role="radiogroup" aria-label="Why?" className="flex flex-col gap-2.5">
          {DECLINE_REASONS.map((one) => {
            const on = reason === one.value
            return (
              <label key={one.value} className={choiceRow(on)}>
                <input
                  type="radio"
                  name="decline-reason"
                  className="peer sr-only"
                  checked={on}
                  onChange={() => setReason(one.value)}
                />
                <span aria-hidden="true"
                  className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface',
                    'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-ring',
                    on ? 'border-2 border-brand-ui' : 'border-[1.6px] border-text-faint')}>
                  {on && <i className="block h-3 w-3 rounded-full bg-brand-ui" />}
                </span>
                {one.label}
              </label>
            )
          })}
        </div>
      )}

      <label className="flex flex-col gap-1.5">
        <span className="sr-only">
          {kind === 'question' ? 'Your question'
            : kind === 'changes' ? 'What would you like changed?'
              : 'Anything you would like to add? (optional)'}
        </span>
        <textarea
          value={body}
          rows={kind === 'decline' ? 3 : 4}
          maxLength={kind === 'decline' ? 1000 : 2000}
          placeholder={kind === 'question' ? 'Type your question'
            : kind === 'changes' ? 'What would you like changed?'
              : 'Anything you would like to add? (optional)'}
          onChange={(event) => setBody(event.target.value)}
          className="min-h-[110px] w-full rounded-[12px] border-[1.6px] border-line bg-surface px-4
                     py-3.5 text-[15.5px] leading-[1.5] text-text placeholder:text-text-faint
                     focus:border-brand-ui focus:shadow-[0_0_0_4px_var(--ring)] focus:outline-none"
        />
      </label>

      {kind === 'question' && view.estimator?.phone && (
        <p className="m-0 flex items-center gap-2 text-[13px] text-text-muted">
          <GlyphPhone size={15} />
          <span>
            Prefer to talk? Call{' '}
            <a href={`tel:${view.estimator.phone}`} className="text-text-muted">
              {view.estimator.phone}
            </a>.
          </span>
        </p>
      )}

      {error && <p className="m-0 text-[13.5px] text-danger">{error}</p>}

      {/*
        * 50px buttons, §9.4. Board 7 puts the action first and wide, Cancel
        * beside it — except on a decline, where the wide one is the red
        * outline and the brand fill is **Keep it open**: the page never
        * argues, but it does not make leaving the loudest thing on it either.
        */}
      <div className="flex gap-2.5 pt-1.5">
        <button
          type="button"
          disabled={!ready || busy}
          onClick={() => void send()}
          className={cn('flex h-[50px] flex-1 items-center justify-center rounded-[12px] px-[18px]',
            'text-[14.5px] font-semibold disabled:cursor-default',
            kind === 'decline'
              ? 'border border-danger bg-surface text-danger disabled:border-line disabled:text-text-faint'
              : 'bg-brand text-brand-on disabled:bg-line-soft disabled:text-text-faint')}
        >
          {busy ? 'Sending…'
            : kind === 'question' ? `Send to ${who}`
              : kind === 'changes' ? 'Send request' : 'Decline proposal'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className={cn('flex h-[50px] items-center rounded-[12px] px-[18px] text-[14.5px] font-semibold',
            kind === 'decline'
              ? 'bg-brand text-brand-on'
              : 'border border-line-strong bg-surface text-text')}
        >
          {kind === 'decline' ? 'Keep it open' : 'Cancel'}
        </button>
      </div>
    </div>
  )
}

/** Board 7's choice row: 12px radius, a hairline, and the brand when chosen. */
const choiceRow = (on: boolean): string => cn(
  'flex min-h-[48px] cursor-pointer items-center gap-3 rounded-[12px] px-3.5 py-3 text-[15px] text-text',
  on ? 'border-2 border-brand-ui bg-brand-t1' : 'border-[1.4px] border-line bg-surface',
)
